/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { INodeProps, Module, Node, getTelemetryObject, NodeTag, isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IProductCollectionViewProps, IProductComponentViewProps } from '@msdyn365-commerce-modules/product-collection';

import { IPriceComponentResources } from '@msdyn365-commerce/components';
import { ProductComponent } from './components/product.component';

import { ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';

import { IProductCollectionProps } from '../definition-extensions/product-collection.ext.props.autogenerated';

import { trackViewItemList } from '../utilities/datalayer/index';

const ProductCollectionView: React.FC<IProductCollectionViewProps & IProductCollectionProps<{}>> = props => {
    const {
        heading,
        ProductCollectionContainer,
        SingleSlideCarouselComponentProps,
        GridComponentProps,
        isCarousel,
        products,
        seeAllButton
    } = props;

    const [isViewItemTracked, setIsViewItemTracked] = React.useState<boolean>(false);
    const [isMobileDevice, setIsMobileDevice] = React.useState<boolean>(false);

    React.useEffect(() => {
        const updateDeviceType = () => {
            if (typeof window !== 'undefined') {
                const isMobileByWidth = window.innerWidth < 768;
                setIsMobileDevice(isMobileByWidth);
            } else {
                const browserVariant = isMobile({ variant: VariantType.Browser, context: props.context.request });
                const viewportVariant = isMobile({ variant: VariantType.Viewport, context: props.context.request });

                const mobileCheck =
                    browserVariant === 'xs' ||
                    browserVariant === 'sm' ||
                    browserVariant === 'md' ||
                    viewportVariant === 'xs' ||
                    viewportVariant === 'sm' ||
                    viewportVariant === 'md';

                setIsMobileDevice(mobileCheck);
            }
        };

        updateDeviceType();

        if (typeof window !== 'undefined') {
            const handleResize = () => updateDeviceType();
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
        return undefined;
    }, [props.context]);

    React.useEffect(() => {
        if (products && !isViewItemTracked) {
            setIsViewItemTracked(true);

            trackViewItemList(
                props.context,
                props.config?.productCollection?.products as SimpleProduct[],
                props.config?.heading?.text || '',
                ''
            );
        }
    }, [products]);

    if (!products) {
        props.context.telemetry.error('Product collection content is empty, module wont render.');

        return null;
    }

    const items = _getProductTiles(props);

    return (
        <Module {...ProductCollectionContainer}>
            {heading}
            {seeAllButton}
            {isMobileDevice && isCarousel
                ? _renderHorizontalScroll(items)
                : isCarousel
                ? _renderCarousel(SingleSlideCarouselComponentProps, items)
                : _renderGrid(GridComponentProps, items)}
        </Module>
    );
};

const _renderCarousel = (carouselContainer: INodeProps, items: IProductComponentViewProps[]): JSX.Element => {
    return <Node {...carouselContainer}>{items && items.map(_renderProduct)}</Node>;
};

const _renderGrid = (gridContainer: INodeProps, items: IProductComponentViewProps[]): JSX.Element => {
    return <Node {...gridContainer}>{items && items.map(_renderProduct)}</Node>;
};

const _renderHorizontalScroll = (items: IProductComponentViewProps[]): JSX.Element => {
    return (
        <Node className='ms-product-collection__mobile-scroll'>
            <Node className='ms-product-collection__items' tag='ul'>
                {items && items.map(_renderProduct)}
            </Node>
        </Node>
    );
};

const _renderProduct = (product: IProductComponentViewProps): JSX.Element => {
    const { ProductContainer, productComponent } = product;

    return <Node {...ProductContainer}>{productComponent}</Node>;
};

const _getProductTiles = (props: IProductCollectionViewProps & IProductCollectionProps<{}>): IProductComponentViewProps[] => {
    const updatedProducts = props.config?.productCollection?.products?.map((item: ProductSearchResult, index: number) => {
        const getProductData = _getProduct(props, item, index);
        return !ObjectExtensions.isNullOrUndefined(getProductData) ? getProductData : undefined;
    });

    // Filter out 'undefined' values. Now, 'products' is guaranteed to be of type IProductComponentViewProps[]
    const productsView: IProductComponentViewProps[] = updatedProducts.filter((item): item is IProductComponentViewProps => {
        return item !== undefined;
    });
    return productsView;
};

const _getProduct = (
    props: IProductCollectionViewProps & IProductCollectionProps<{}>,
    product: ProductSearchResult,
    index: number
): IProductComponentViewProps | null => {
    if (!product || !product.Name) {
        props.telemetry.trace('[product-collection] Product or product name is null');
        return null;
    }
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const { imageSettings, allowBackNavigation } = props.config;
    const resources = props.resources;
    const breaCrumbType = props.context.app.config.breadcrumbType;
    const allowBack = allowBackNavigation && (breaCrumbType === 'back' || breaCrumbType === 'categoryAndBack');
    const quickviewSlot = props.slots && props.slots.quickview && props.slots.quickview.length > 0 ? props.slots.quickview[0] : null;
    const productComparisonButtonSlot = ArrayExtensions.hasElements(props.slots.productComparisonButton)
        ? props.slots.productComparisonButton[0]
        : null;

    const reviewCount = 1;
    const reviewTotalRatings = Number(product.TotalRatings);
    const reviewCountAriaLabelValue =
        reviewTotalRatings > reviewCount ? resources.ratingCountAriaLabel : resources.ratingCountAriaLabelSingleUser;
    const isPriceMinMaxEnabled = props.data.featureState.result?.find(
        featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
    );
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };
    return {
        ProductContainer: {
            tag: 'li' as NodeTag,
            className: 'ms-product-collection__item',
            role: 'listitem',
            key: index
        },
        productComponent: (
            <ProductComponent
                context={props.context}
                telemetryContent={telemetryContent}
                imageSettings={imageSettings}
                freePriceText={resources.priceFree}
                originalPriceText={resources.originalPriceText}
                currentPriceText={resources.currentPriceText}
                ratingAriaLabel={resources.ratingAriaLabel}
                ratingCountAriaLabel={reviewCountAriaLabelValue}
                allowBack={allowBack}
                id={props.id}
                typeName={props.typeName}
                data={{ product }}
                quickViewButton={quickviewSlot}
                productComparisonButton={productComparisonButtonSlot}
                isPriceMinMaxEnabled={isPriceMinMaxEnabled?.IsEnabled}
                priceResources={priceResources}
                //TODO removed the dimensionAvailabilities feature for now
                dimensionAvailabilities={undefined}
                productCategoryUserPath=''
                productListName={props.config.heading ? props.config.heading.text : ''}
                resources={resources}
            />
        )
    };
};
export default ProductCollectionView;
